import { memo, useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box } from '@mui/material';
import { FormContainer } from 'components/layout';
import { AdAccountSelect } from 'components/common';
import type { Option } from 'components/common';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';
import { usePinterestAccounts } from '../api';
import { clearPinterestDownstreamSelections } from '../utils/formSelections';

const ChooseAccount = memo(() => {
  const { setValue } = useFormContext();
  const app = (useWatch({ name: 'app' }) as string | undefined) ?? '';
  const accountId = (useWatch({ name: 'accountId' }) as string | undefined) ?? '';
  const {
    setAdAccount: setAdAccountParam,
    setSelectedCampaigns: setSelectedCampaignsParam,
    setSelectedAdSets: setSelectedAdSetsParam,
    setFilterCampaignsByName: setFilterCampaignsByNameParam,
    setFilterAdSetsByName: setFilterAdSetsByNameParam,
  } = useLocationParams();

  const { data, isLoading, isError, error } = usePinterestAccounts<Option[]>({
    params: { app },
    config: {
      enabled: !!app,
      select: (accounts) => accounts.map((account) => ({ value: account.ad_account_id, label: account.name })),
    },
  });

  const handleChangeAccount = useCallback(() => {
    clearPinterestDownstreamSelections(setValue);
    setSelectedCampaignsParam([]);
    setSelectedAdSetsParam([]);
    setFilterCampaignsByNameParam([]);
    setFilterAdSetsByNameParam([]);
  }, [setValue, setFilterAdSetsByNameParam, setFilterCampaignsByNameParam, setSelectedAdSetsParam, setSelectedCampaignsParam]);

  useEffect(() => {
    setAdAccountParam(accountId || undefined);
  }, [accountId, setAdAccountParam]);

  useEffect(() => {
    if (!isError || !error) return;

    datadogActions.trackDatadogError(error as Error, { source: 'pinterest_ad_account_select_query', app });
  }, [isError, error, app]);

  return (
    <FormContainer title="Choose Ad Account">
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
        <Box sx={{ display: 'flex', flex: 1 }}>
          <AdAccountSelect
            name="accountId"
            inputLabel="Ad Account"
            options={data}
            isLoading={isLoading}
            onChange={handleChangeAccount}
          />
        </Box>
        <Box sx={{ display: 'flex', flex: 1 }} />
      </Box>
    </FormContainer>
  );
});

ChooseAccount.displayName = 'ChooseAccount';

export default ChooseAccount;
